import {Component, OnInit} from '@angular/core';
import {IEmployeePermissionGroup, IEmployeeRole, IPermissionGroup, IPermissionOption} from "../../../shared/models";
import {Subject, Subscription} from "rxjs";
import {
  Config,
  getConfig, getEditingBusinessUnitEmployee,
  getLookups,
  getSelectedBusinessUnit,
  initialConfigState,
  initialLookupsState,
  Lookups, toggleLoading
} from "../../../redux";
import {UserCardSize} from "../../../shared/components";
import {FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {Store} from "@ngrx/store";
import {Router} from "@angular/router";
import {ToastrService} from "ngx-toastr";
import {MatDialog} from "@angular/material/dialog";
import {UtilService} from "../../../shared/services";
import {BusinessUnitService} from "../../services";
import {IStructureResponse} from "../../models";

@Component({
  selector: 'app-business-unit-employee-permissions',
  templateUrl: './business-unit-employee-permissions.component.html',
  styleUrls: ['./business-unit-employee-permissions.component.scss']
})
export class BusinessUnitEmployeePermissionsComponent implements OnInit {

  config$: Subscription = new Subscription();
  lookup$: Subscription = new Subscription();
  selectedBusinessUnit$: Subscription = new Subscription();
  selectedEmployee$: Subscription = new Subscription();

  allRole$: Subscription = new Subscription();
  allRolesSubject = new Subject<IEmployeeRole[]>();

  private config: Config = initialConfigState;
  lookups: Lookups = initialLookupsState;

  userCardSize = UserCardSize;
  // permissions
  allRoles: IEmployeeRole[] = [];
  selectedRole: IEmployeeRole | undefined = undefined;
  selectedOptions: Map<string, IPermissionOption> = new Map<string, IPermissionOption>();

  selectedEmployee: any = {} as unknown as any;
  employeeRole: any = {} as unknown as IEmployeeRole;
  structure: IStructureResponse = {} as unknown as IStructureResponse;

  permissions: string[] = [];

  //reactive form
  userForm = new FormGroup({
    role: new FormControl(),
  });
  form: FormGroup = new FormGroup({});

  constructor(
    private store: Store,
    private router: Router,
    private toastr: ToastrService,
    private dialog: MatDialog,
    public util: UtilService,
    private businessUnitService: BusinessUnitService,
    private fb: FormBuilder
  ) {
    this.config$ = this.store.select(getConfig).subscribe(config => {
      this.config = config;
    });
    this.lookup$ = this.store.select(getLookups).subscribe(lookups => {
      this.lookups = lookups;
    });
    this.selectedBusinessUnit$ = this.store.select(getSelectedBusinessUnit).subscribe(selectedBusinessUnit => {
      if (!!selectedBusinessUnit && !!selectedBusinessUnit.businessUnitId) {
        this.subscribeToDatasource();
      }
    });

    // get all available roles when employee is selected
    this.selectedEmployee$ = this.store.select(getEditingBusinessUnitEmployee).subscribe((employee: any) => {
      this.selectedEmployee = employee;
      this.getAllRoles();
      this.getEmployeePermissions();
    });

    // fetch the employee role only after all available roles have been updated
    this.allRole$ = this.allRolesSubject.subscribe((roles: IEmployeeRole[]) => {
      this.getEmployeeRole();
    });
  }

  ngOnInit() {

  }

  ngOnDestroy() {
    this.config$.unsubscribe();
    this.lookup$.unsubscribe();
    this.selectedBusinessUnit$.unsubscribe();
    this.selectedEmployee$.unsubscribe();
    this.allRole$.unsubscribe();
  }

  subscribeToDatasource() {
    this.getStructure();
  }

  /**
   * Get all available roles
   */
  getAllRoles() {
    if (!!this.selectedEmployee?.user) {
      this.businessUnitService.getAllRoles(this.selectedEmployee?.id).subscribe({
        next: (roles: IEmployeeRole[]) => {
          if (!!roles) {
            this.allRoles = roles;
            this.allRolesSubject.next(this.allRoles);
          }
        }, error: (err: any) => {
          this.toastr.error("Unable to get roles!", $localize`:@@companyName:Rondeivu`);
        }
      });
    }
  }

  /**
   * Get the current employee role
   */
  getEmployeeRole() {
    this.store.dispatch(toggleLoading({loading: true}));
    this.businessUnitService.getEmployeeRoles(this.selectedEmployee?.id).subscribe({
      next: (employeeRole: IEmployeeRole) => {
        if (!!employeeRole?.id) {
          this.selectRoleById(employeeRole.id);
        }
        this.store.dispatch(toggleLoading({loading: false}));
      }, error: (err: any) => {
        this.store.dispatch(toggleLoading({loading: false}));
        this.toastr.error("Unable to get user role!", $localize`:@@companyName:Rondeivu`);
      }
    });
  }

  /**
   * Get the current employee permissions
   */
  getEmployeePermissions() {
    this.store.dispatch(toggleLoading({loading: true}));
    this.businessUnitService.getEmployeePermissions(this.selectedEmployee?.id).subscribe({
      next: (permissions: string[]) => {
        this.permissions = permissions;
        this.store.dispatch(toggleLoading({loading: false}));
      }, error: (err: any) => {
        this.store.dispatch(toggleLoading({loading: false}));
        this.toastr.error("Unable to get user role!", $localize`:@@companyName:Rondeivu`);
      }
    });
  }

  private selectRoleById(id: string) {
    for (let i = 0; i <= this.allRoles.length - 1; i++) {
      if (id == this.allRoles[i].id) {
        this.userForm.get('role')?.patchValue(this.allRoles[i]);
        this.updateFormRole(this.allRoles[i]);
      }
    }
  }

  /**
   * Get the form structure to create
   */
  getStructure() {
    this.businessUnitService.getPageStructure().subscribe({
      next: (structure: IStructureResponse) => {
        this.structure = structure;
        this.form = this.fb.group(this.getFormFields());
      }, error: (err: any) => {
        this.toastr.error("Unable to get page options!", $localize`:@@companyName:Rondeivu`);
      }
    });
  }

  private getFormFields() {
    let formGroupFields = {};
    this.structure.businessUnitPermissionGroups.forEach((buPg: IPermissionGroup) => {
      // @ts-ignore
      formGroupFields[`${buPg.id}`] = new FormControl('');
    });
    this.structure.globalDealTeamPermissionGroups.forEach((gdtPg: IPermissionGroup) => {
      // @ts-ignore
      formGroupFields[`${gdtPg.id}`] = new FormControl('');
    });
    return formGroupFields;
  }

  /**
   * Navigate back
   */
  back() {
    this.store.dispatch(toggleLoading({loading: true}));
    this.router.navigate(['/' + this.config.selected_business_unit.slug + '/settings/employees/']).then(() => {
      this.store.dispatch(toggleLoading({loading: false}));
    });
  }

  /**
   * Update the generated form with the role options
   * @param event
   */
  updateForm(event: any) {
    //update the form
    const r = event.value as IEmployeeRole;
    this.updateFormRole(r);
  }

  private updateFormRole(role: IEmployeeRole) {
    this.selectedRole = role;
    this.selectedRole.permissionGroups.forEach((pg: IEmployeePermissionGroup) => {
      //find the option
      let option = this.getPermissionGroupOption(pg.permissionGroupId, pg.permissionGroupOptionId);
      //update the dynamic form
      this.form.get(`${pg.permissionGroupId}`)?.patchValue(option);
      //add the selection to the option map
      if (this.selectedOptions.has(pg.permissionGroupId)) {
        this.selectedOptions.delete(pg.permissionGroupId);
      }
      this.selectedOptions.set(pg.permissionGroupId, option);
    });
    // console.log(this.form.getRawValue());
  }

  //helper to get the option object by ids
  private getPermissionGroupOption(permissionGroupId: string, permissionGroupOptionId: string): IPermissionOption {
    let rtnOption = {} as unknown as IPermissionOption;
    if (!!this.structure) {
      //bu options
      this.structure.businessUnitPermissionGroups.forEach((pg: IPermissionGroup) => {
        if (permissionGroupId == pg.id) {
          pg.permissionOptions?.forEach((opt: IPermissionOption) => {
            if (opt.id == permissionGroupOptionId) {
              rtnOption = opt;
            }
          });
        }
      });
      //deal options
      this.structure.globalDealTeamPermissionGroups.forEach((pg: IPermissionGroup) => {
        if (permissionGroupId == pg.id) {
          pg.permissionOptions?.forEach((opt: IPermissionOption) => {
            if (opt.id == permissionGroupOptionId) {
              rtnOption = opt;
            }
          });
        }
      });
    }
    // console.log(rtnOption);
    return rtnOption;
  }

  /**
   * Save the employee role
   */
  savePermissions() {
    this.store.dispatch(toggleLoading({loading: true}));
    this.businessUnitService.saveEmployeeRoles(this.selectedEmployee?.id, this.selectedRole).subscribe({
      next: (role: IEmployeeRole) => {
        if (!!role && role.isCustomRole) {
          this.setCustomRoleId(role.id);
        }
        this.store.dispatch(toggleLoading({loading: false}));
        this.toastr.success("Employee Permissions Updated", $localize`:@@companyName:Rondeivu`);
      }, error: (err: any) => {
        this.store.dispatch(toggleLoading({loading: false}));
        this.toastr.error("Unable to get update employee permissions!", $localize`:@@companyName:Rondeivu`);
      }
    });
  }

  /**
   * When an employee permission changes
   * @param permissionGroupId
   * @param event
   */
  onChange(permissionGroupId: string, event: any) {
    const option = event.value as unknown as IPermissionOption;

    //add the selection to the option map
    if (this.selectedOptions.has(permissionGroupId)) {
      this.selectedOptions.delete(permissionGroupId);
    }
    this.selectedOptions.set(permissionGroupId, option);

    //get the custom role
    let customRole = this.getCustomRole();
    // clone the currently selected role
    customRole.permissionGroups = JSON.parse(JSON.stringify(this.selectedRole?.permissionGroups));
    // find the permission group being changed
    // replace the permission group option to the event payload
    customRole.permissionGroups.forEach((pg: { permissionGroupId: string, permissionGroupOptionId: string }) => {
      if (pg.permissionGroupId == permissionGroupId) {
        pg.permissionGroupOptionId = option.id;
      }
    });

    // rename the custom role
    if (!this.selectedRole?.isCustomRole) {
      customRole.name = 'Custom [' + this.selectedEmployee?.user?.firstName + ' ' + this.selectedEmployee?.user?.lastName + ']';
    }

    // add the custom role
    if (!this.hasCustomRole()) {
      this.allRoles.push(customRole);
    }

    this.selectedRole = customRole;
    this.userForm.get('role')?.patchValue(this.selectedRole);
  }

  private hasCustomRole(): boolean {
    for (let i = 0; i <= this.allRoles.length - 1; i++) {
      if (this.allRoles[i].isCustomRole) {
        return true;
      }
    }
    return false;
  }

  private getCustomRole(): IEmployeeRole {
    for (let i = 0; i <= this.allRoles.length - 1; i++) {
      if (this.allRoles[i].isCustomRole) {
        return this.allRoles[i];
      }
    }
    return {
      id: '',
      description: 'This is a custom user permission profile',
      isCustomRole: true,
      permissionGroups: []
    } as unknown as IEmployeeRole;
  }

  private setCustomRoleId(id: string) {
    for (let i = 0; i <= this.allRoles.length - 1; i++) {
      if (this.allRoles[i].isCustomRole) {
        this.allRoles[i].id = id;
      }
    }
  }

}
